import clsx from 'clsx';
import type * as React from 'react';

import * as api from '@gitbook/api-client';
import { Icon, type SVGIconComponent, type SVGIconProps } from '@gitbook/spine-icons';
import {
    Card,
    CardBody,
    CardCheck,
    CardDetailedHeader,
    CardHighlight,
    CardRadio,
    Description,
    Group,
    Heading,
    HoverCard,
    HoverCardRoot,
    HoverCardTrigger,
    ProgressiveImage,
    renderIcon,
    Tooltip,
} from '@gitbook/spine-react';

import { DocumentationLink } from '../Link';
import siteSectionsImageDark from '../Site/assets/site-section-intro-dark.raw.svg';
import siteSectionsImageLight from '../Site/assets/site-section-intro-light.raw.svg';

export type PlanFeature = {
    key: string;
    title: string;
};

export function PlanCardRadio(props: {
    plan: api.SiteType | api.BillingProduct;
    title: React.ReactNode;
    description: string;
    price?: {
        amount: number | string;
        period: 'month' | 'year';
    };
    featuresList?: React.ReactNode;
    footer?: React.ReactNode;
    cardHighlight?: string;
    className?: string;
}) {
    const { plan, title, description, price, featuresList, footer, cardHighlight, className } =
        props;

    return (
        <CardRadio
            autoFocus
            value={plan}
            className={clsx('flex flex-col justify-between', className)}
        >
            {cardHighlight ? (
                <CardHighlight className="px-4 py-2 text-ui-small">{cardHighlight}</CardHighlight>
            ) : null}
            <div className="divide-y">
                <div className="flex w-full items-center gap-6 p-4">
                    <CardDetailedHeader
                        title={title}
                        description={description}
                        details={
                            price ? (
                                <div className="flex flex-col items-end justify-center">
                                    <span className="text-ui-heading-small">
                                        {typeof price.amount === 'string'
                                            ? price.amount
                                            : price.amount > 0
                                              ? `$${price.amount}`
                                              : 'Free'}
                                    </span>
                                    {typeof price.amount === 'string' || price.amount > 0 ? (
                                        <p className="text-nowrap text-muted text-ui-base">
                                            per {price.period}
                                        </p>
                                    ) : null}
                                </div>
                            ) : null
                        }
                    />
                    <CardCheck />
                </div>
                {featuresList ? <div className="p-4">{featuresList}</div> : null}
                {footer ? <div className="p-4">{footer}</div> : null}
            </div>
        </CardRadio>
    );
}

export type FeatureListIcon = {
    icon: SVGIconComponent;
    props: Omit<SVGIconProps, 'size'>;
};

export interface PlanCardRadioFeaturesListProps {
    features: Array<PlanFeature>;
    moreFeatures?: Array<PlanFeature>;
    columns?: number;
    featureListIcon?: FeatureListIcon;
}

export function PlanCardRadioFeaturesList(props: PlanCardRadioFeaturesListProps) {
    const { features, moreFeatures, columns, featureListIcon } = props;
    const FeatureListIcon = featureListIcon?.icon ?? Icon.Check;

    return (
        <ul
            className="w-full columns-2 md:columns-[--column-count]"
            style={
                {
                    '--column-count': columns,
                } as React.CSSProperties
            }
        >
            {features.map((feature) => (
                <li key={feature.key} className="flex items-center gap-1 pt-1 text-muted">
                    {renderIcon(FeatureListIcon, {
                        size: 16,
                        className: 'shrink-0 text-primary',
                        ...featureListIcon?.props,
                    })}
                    <PlanCardFeatureTitle feature={feature} />
                </li>
            ))}
            {moreFeatures?.length ? (
                <Tooltip
                    label={moreFeatures.map((feature) => feature.title).join(', ')}
                    delay={300}
                >
                    <span className="flex flex-row items-center gap-1 p-1 text-muted underline decoration-dotted">
                        … and more <Icon.InfoCircle size={14} />
                    </span>
                </Tooltip>
            ) : null}
        </ul>
    );
}

const planCardRadioHoverCards: Partial<{ [key in api.SiteFeatureKey]: React.ReactNode }> = {
    [api.SiteFeatureKey.SitesSections]: (
        <Card>
            <CardBody>
                <Heading>Bring even more structure to your site</Heading>
                <Description>
                    Organize content into sections and variants to showcase every aspect of your
                    product in a single docs site.
                </Description>
                <Description>
                    Check our{' '}
                    <DocumentationLink path="publishing-documentation/site-structure/site-sections">
                        docs
                    </DocumentationLink>{' '}
                    for more info.
                </Description>
                <ProgressiveImage
                    src={{ light: siteSectionsImageLight, dark: siteSectionsImageDark }}
                    width="100%"
                    height="auto"
                    style={{ aspectRatio: '450 / 153' }}
                />
            </CardBody>
        </Card>
    ),

    [api.SiteFeatureKey.SitesVisitorAuthentication]: (
        <Card>
            <CardBody>
                <Heading>Secure your content</Heading>
                <Description>
                    Set up your authentication with our Auth0, Azure AD or Okta integrations, or get
                    up and running with your own custom backend if you want even more control.
                </Description>
                <Description>
                    Check our{' '}
                    <DocumentationLink path="publishing-documentation/visitor-authentication">
                        docs
                    </DocumentationLink>{' '}
                    for more info.
                </Description>
            </CardBody>
        </Card>
    ),
};

interface PlanCardFeatureTitleProps {
    feature: PlanFeature;
}

function PlanCardFeatureTitle(props: PlanCardFeatureTitleProps) {
    const { feature } = props;
    const featureTitle = <span className="flex gap-2 whitespace-nowrap">{feature.title}</span>;
    const hoverCard = planCardRadioHoverCards[feature.key];

    if (!hoverCard) {
        return featureTitle;
    }

    return (
        <HoverCardRoot>
            <HoverCardTrigger>
                <Group>
                    {featureTitle}
                    <Icon.InfoCircle />
                </Group>
            </HoverCardTrigger>
            {hoverCard ? <HoverCard className="max-w-96">{hoverCard}</HoverCard> : null}
        </HoverCardRoot>
    );
}
